import { Injectable } from '@angular/core';
import { LoggerService } from '@core/services/logger/logger.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { distinctUntilChanged, map, shareReplay } from 'rxjs/operators';
import { combineLatest, filter, Observable, of } from 'rxjs';
import { OrderQuery } from '@modules/order/state/order-query';
import { OrderStore } from '@modules/order/state/order-store';
import { ProcedureConfigurationService } from '@modules/order/services/procedure-configuration.service';
import { ProcedureFormStateService } from '@shared/services/procedure-form-state.service';
import { OrderFacadeBase } from '@modules/order/order.facade.base';
import { ShellStore } from '@shared/store/shell/shell-store';
import { SendToOption } from '@shared/models/send-to-option';
import { ProcedureTilesService } from '@modules/rx-intuitive/services/procedure-tiles.service';
import { PopUpActions } from '@shared/models/enums/popup-modal-actions.enum';
import {
	ConfimationDialogData,
	ConfirmationDialogComponent
} from '@shared/components/toolkit-confirmation-dialog/confirmation-dialog.component';
import { TlkModalWindowService } from '@itero/ui-toolkit-angular/modal-window';
import { SendToIdForNotLabsEnum } from '@modules/order/models/send-to-type.enum';
import { TeethDiagramQuery } from '@modules/teeth-diagram/state/teeth-diagram-query';
import { RxIntuitiveStoresResetService } from '@shared/services/rx-intuitive-stores-reset.service';
import { RxIntuitiveTeethDiagramQuery } from '@modules/rx-intuitive-teeth-diagram/state/rx-intuitive-teeth-diagram-query';

@Injectable()
export class RxIntuitiveOrderFacade extends OrderFacadeBase {
	procedureMap$ = this.orderQuery.procedureMap$;
	isReadOnly$ = this.shellQuery.getReadOnlyState('order');
	isReturned$ = this.shellQuery.isReturned$;
	defaultProcedure$ = this.procedureConfigurationService.defaultProcedure$;

	sendToState$ = combineLatest([this.orderQuery.procedureMap$, this.shellQuery.shouldValidateForSend$]).pipe(
		map(([pm, shouldValidateForSend]) => this.procedureFormStateService.getSendToState(pm, shouldValidateForSend)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	dueDateState$ = combineLatest([this.orderQuery.procedureMap$, this.isReadOnly$]).pipe(
		map(([pm, isReadOnly]) => this.procedureFormStateService.getDueDateState(pm, isReadOnly)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	isSendToCancellable$ = combineLatest([this.orderQuery.procedureMap$, this.orderQuery.availableProcedureMaps$]).pipe(
		map(([pm, availableProcedureMaps]) => this.procedureFormStateService.isSendToCancellable(pm, availableProcedureMaps)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	procedureTiles$ = this.procedureTilesService.procedureTiles$;
	availableLabs$ = this.procedureConfigurationService.sendToOptions$;
	minimalAvailableDueDate$ = this.orderQuery.minimalAvailableDueDate$;
	sendTo$ = this.orderQuery.sendTo$;
	dueDate$ = this.orderQuery.dueDate$;
	userRole$ = this.shellQuery.userRole$;

	selectedProcedureId$ = this.procedureMap$.pipe(
		map(procedureMap => procedureMap?.ProcedureId),
		distinctUntilChanged(),
		shareReplay({ bufferSize: 1, refCount: true })
	);
	selectedProcedureTile$ = combineLatest([this.procedureTiles$, this.selectedProcedureId$]).pipe(
		distinctUntilChanged(),
		map(([procedureTiles, selectedProcedureId]) =>
			selectedProcedureId ? procedureTiles.find(tile => tile.Id === selectedProcedureId) : undefined
		)
	);

	get procedure() {
		return this.orderQuery.procedure;
	}

	constructor(
		protected readonly orderStore: OrderStore,
		protected readonly orderQuery: OrderQuery,
		protected readonly shellStore: ShellStore,
		protected readonly shellQuery: ShellQuery,
		protected readonly logger: LoggerService,
		private readonly procedureConfigurationService: ProcedureConfigurationService,
		private readonly procedureTilesService: ProcedureTilesService,
		private readonly procedureFormStateService: ProcedureFormStateService,
		private readonly modalService: TlkModalWindowService,
		private readonly teethDiagramQuery: TeethDiagramQuery,
		private readonly rxStoresResetService: RxIntuitiveStoresResetService,
		private readonly intuitiveDiagramQuery: RxIntuitiveTeethDiagramQuery
	) {
		super(orderStore, orderQuery, shellQuery, logger);
	}

	handleProcedureChanged(procedureId: number): void {
		const currentProcedureId = this.orderQuery.procedureMap?.ProcedureId;

		if (currentProcedureId === procedureId) {
			return;
		}

		const procedureMap = this.procedureConfigurationService.getProcedureMap(procedureId);

		this.rxStoresResetService.resetDependingStores();
		this.orderStore.update({
			procedureMap,
			sendTo: null,
			dueDate: null,
			isMultiBiteSelected: undefined
		});
	}

	handleSendToChanged(sendTo: SendToOption): Observable<SendToOption | null> {
		if (sendTo?.Id === this.orderQuery.sendTo?.Id) {
			return of(sendTo);
		}

		if (
			sendTo?.Id === SendToIdForNotLabsEnum.glidewell &&
			(this.teethDiagramQuery.getIsDirty() || this.intuitiveDiagramQuery.getIsDirty())
		) {
			return this.promptSendToChange().pipe(
				map(result => {
					if (result === PopUpActions.Ok) {
						this.rxStoresResetService.resetDependingStores();
						this.updateSendTo(sendTo);

						return sendTo;
					} else {
						this.updateSendTo(this.orderQuery.sendTo);

						return this.orderQuery.sendTo;
					}
				})
			);
		} else {
			this.updateSendTo(sendTo);

			return of(sendTo);
		}
	}

	handleDueDateChanged(dueDate: Date): void {
		this.orderStore.update({ dueDate });
	}

	private promptSendToChange(): Observable<PopUpActions> {
		const dialogRef = this.modalService.open<PopUpActions, ConfimationDialogData, ConfirmationDialogComponent>(
			ConfirmationDialogComponent,
			{
				data: {
					titleTranslationKey: 'FlexibleRxProcedure.ChangeSendToTitle',
					contentTranslationKey: 'FlexibleRxProcedure.ChangeProcedureMessage',
					confirmTranslationKey: 'FlexibleRxProcedure.ChangeSendToOkButton',
					cancelTranslationKey: 'FlexibleRxProcedure.ChangeProcedureCancelButton'
				},
				panelClass: 'change-confimation-dialog'
			}
		);

		return dialogRef.closed.pipe(filter((result): result is PopUpActions => result !== undefined));
	}

	private updateSendTo(sendTo: SendToOption | null): void {
		const { ProcedureId, TypeId } = this.orderQuery.procedureMap ?? {};

		if (ProcedureId == null) {
			this.orderStore.update({ sendTo });

			return;
		}

		const procedureMap = this.procedureConfigurationService.getProcedureMap(ProcedureId, TypeId, sendTo);

		if (procedureMap?.Id !== this.orderQuery.procedureMap?.Id) {
			this.orderStore.update({ procedureMap, sendTo });

			return;
		}
		this.orderStore.update({ sendTo });
	}
}
